import React, { useEffect, useState } from "react";
import { FileText, X, GraduationCap, User, BookMarked, ExternalLink, CheckCircle2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-50 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-[#012a1e] font-medium text-right">{value || "—"}</span>
  </div>
);

export default function StudentProfileView({ open, onClose, studentId, email }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [unis, setUnis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");
  const [reviewing, setReviewing] = useState("");

  useEffect(() => {
    if (!open) return;
    setData(null); setLoading(true); setNote("");
    const params = studentId ? `sid=${studentId}` : `email=${encodeURIComponent(email || "")}`;
    api.get(`/portal/staff/student-profile?${params}`).then((r) => setData(r.data)).catch(() => setData({ found: false })).finally(() => setLoading(false));
    api.get("/universities").then((r) => setUnis(r.data)).catch(() => {});
  }, [open, studentId, email]);

  const p = data?.profile || {};
  const uni = unis.find((u) => u.id === p.institute_of_choice);

  const review = async (status) => {
    setReviewing(status);
    try {
      const { data: r } = await api.post("/portal/staff/student-review", { student_id: data?.student?.id, email: data?.student?.email, status, note });
      setData((d) => ({ ...d, profile: { ...d.profile, review_status: status, review_note: note } }));
      toast({ title: `Marked ${status}`, description: r.email_sent ? "Student notified by email." : "Saved (email not sent — Resend not configured)." });
      setNote("");
    } catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setReviewing(""); }
  };

  const REVIEW_COLORS = { Approved: "bg-green-100 text-green-700", Rejected: "bg-red-100 text-red-700", Reviewed: "bg-blue-100 text-blue-700", "Offer Received": "bg-teal-100 text-teal-700" };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="student-profile-view">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><User size={18} /> Student Profile</DialogTitle></DialogHeader>
        {loading ? <p className="text-slate-400 py-8 text-center">Loading…</p>
          : !data?.found ? <p className="text-slate-400 py-8 text-center">No portal profile found for this student.</p>
            : (
              <div className="space-y-5 py-1">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-[#012a1e]">{data.student.name}</h3>
                    {data.student.student_code && <span className="font-mono text-xs bg-[#f0f6f3] text-[#004030] px-2 py-0.5 rounded">{data.student.student_code}</span>}
                  </div>
                  <Row label="Email" value={data.student.email} />
                  <Row label="Gender" value={p.gender} />
                  <Row label="Mobile" value={p.mobile ? `${p.dial_code || ""} ${p.mobile}` : ""} />
                  <Row label="ID" value={p.id_category ? `${p.id_category}: ${p.id_number || ""}` : ""} />
                  <Row label="Nationality" value={p.nationality} />
                  <Row label="Address" value={[p.address, p.city, p.country].filter(Boolean).join(", ")} />
                  <Row label="Agent code" value={data.student.agent_code} />
                </div>

                {/* Counsellor review */}
                <div className="rounded-xl border border-slate-100 bg-[#f7faf8] p-4" data-testid="review-panel">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-[#012a1e]">Counsellor review</span>
                    {p.review_status && <span data-testid="review-status" className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${REVIEW_COLORS[p.review_status] || "bg-slate-100 text-slate-600"}`}>{p.review_status}</span>}
                  </div>
                  {p.review_note && <p className="text-xs text-slate-500 mb-2">Last note: {p.review_note}</p>}
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note to the student…" className="h-10 mb-2" data-testid="review-note" />
                  <div className="flex flex-wrap gap-2">
                    {[["Reviewed", "bg-blue-600"], ["Approved", "bg-green-600"], ["Offer Received", "bg-teal-600"], ["Rejected", "bg-red-600"]].map(([s, c]) => (
                      <Button key={s} size="sm" disabled={!!reviewing} onClick={() => review(s)} data-testid={`review-${s.split(" ")[0].toLowerCase()}`}
                        className={`${c} hover:opacity-90 text-white rounded-full text-xs h-8`}>
                        {reviewing === s ? <Loader2 className="animate-spin" size={13} /> : s}
                      </Button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">The student is emailed their new status automatically.</p>
                </div>

                <div>
                  <h4 className="font-semibold text-[#012a1e] flex items-center gap-2 mb-2"><GraduationCap size={16} /> Academic History</h4>
                  {(p.education_history || []).length === 0 ? <p className="text-sm text-slate-400">None added.</p>
                    : (p.education_history || []).map((e, i) => (
                      <div key={i} className="border border-slate-100 rounded-lg p-3 mb-2 bg-[#f9fcfb]">
                        <p className="text-sm font-medium text-[#012a1e]">{[e.course, e.level].filter(Boolean).join(" · ") || "Qualification"}</p>
                        <p className="text-xs text-slate-500">{[e.stream, e.institution === "Other" ? e.institution_other : e.institution, e.country].filter(Boolean).join(" · ")}</p>
                        <p className="text-xs text-slate-500 mt-0.5">Year: {e.year_of_passing || "—"} · Result: {e.result || "—"}{e.result === "Failed" ? ` · Arrears: ${e.arrears || 0}` : ""}</p>
                        {(e.certificates || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {e.certificates.map((c, ci) => (
                              <a key={ci} href={c.data} target="_blank" rel="noreferrer" className="border border-slate-200 rounded-lg p-1.5 w-20 text-center bg-white hover:border-[#004030]" data-testid={`view-cert-${i}-${ci}`}>
                                {c.kind === "image" ? <img src={c.data} alt={c.name} className="h-12 w-full object-cover rounded" /> : <FileText size={24} className="mx-auto text-[#004030]" />}
                                <p className="text-[9px] truncate mt-0.5 flex items-center justify-center gap-0.5">{c.name}<ExternalLink size={8} /></p>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                </div>

                <div>
                  <h4 className="font-semibold text-[#012a1e] flex items-center gap-2 mb-2"><BookMarked size={16} /> Programme Info</h4>
                  <Row label="Institute of choice" value={uni ? `${uni.name} — ${uni.country}` : ""} />
                  <Row label="Campus" value={p.campus} />
                  <Row label="Mode / Type" value={[p.programme_mode, p.programme_type].filter(Boolean).join(" · ")} />
                  <Row label="Programme apply" value={p.programme_of_choice} />
                  <Row label="Preferred intake" value={p.preferred_intake} />
                  <Row label="Remarks" value={p.remarks} />
                </div>
              </div>
            )}
      </DialogContent>
    </Dialog>
  );
}
